import request from "../../utils/request";
import type { ChannelPreference, NotificationPreferenceItemInput } from "./NotificationPreference";

/**
 * CO-375 / owner decision E13 — a company's notification MODULES (NotAsked / On / Off) and the tenant
 * master switch. Contract: docs/designs/CO-375-E13-notifications.md §8. The prompt copy is COMPOSED from
 * the cadence / audience / channel enums below, never hand-written prose.
 */
export type NotificationModuleKey =
  | "attendance"
  | "leaves"
  | "payroll"
  | "people"
  | "profile"
  | "engagement"
  | "performance"
  | "tournament"
  | "billing"
  | "projects";

export type NotificationModuleState = "NotAsked" | "On" | "Off";
export type NotificationCadence = "onAction" | "hourly" | "daily" | "weekly" | "scheduled";
export type NotificationAudience = "employee" | "approver" | "hr" | "operator";
export type NotificationChannel = "inApp" | "push" | "email";
/** optIn = never asked; review = backfilled On by the E13 migration; newTypes = On, types added since. */
export type NotificationPromptMode = "optIn" | "review" | "newTypes";

export interface NotificationModuleTypeView {
  id: number;
  name: string;
  cadence: NotificationCadence;
  audience: NotificationAudience[];
  channels: NotificationChannel[];
  isNewSinceAnswer: boolean;
}

export interface NotificationModuleView {
  key: NotificationModuleKey | string;
  state: NotificationModuleState;
  source: string | null;
  answeredById: string | null;
  answeredByName: string | null;
  answeredOn: string | null;
  promptMode: NotificationPromptMode | null;
  webRoutePrefixes: string[];
  mobileScreens: string[];
  types: NotificationModuleTypeView[];
}

export interface NotificationModulesView {
  masterDisabled: boolean;
  /** Owner 2026-09-28: a fake / E2E test company — master starts OFF and also governs must-send emails. */
  isTestCompany?: boolean;
  modules: NotificationModuleView[];
}

interface Result<T> {
  payload: T;
  status: number;
  errors: Array<{ description: string }>;
}

export async function getNotificationModules() {
  const response = await request.get<Result<NotificationModulesView>>("NotificationModules");
  return response.data;
}

export async function setNotificationModuleState(
  key: string,
  state: "On" | "Off",
  source: "prompt" | "settings"
) {
  const response = await request.put<Result<NotificationModuleView>>(
    `NotificationModules/${encodeURIComponent(key)}`,
    { state, source }
  );
  return response.data;
}

export async function setNotificationMaster(disabled: boolean) {
  const response = await request.put<Result<boolean>>("NotificationModules/master", { disabled });
  return response.data;
}

// ── Operator: own platform-alert preferences (owner Q5, 2026-09-28). Operator policy only. ──────────

export interface OperatorNotificationPreferenceView {
  notificationTypeId: number;
  notificationTypeName: string;
  cadence: NotificationCadence;
  channels: NotificationChannel[];
  email: ChannelPreference;
  push: ChannelPreference;
  inApp: ChannelPreference;
}

export async function getOperatorNotificationPreferences() {
  const response = await request.get<Result<OperatorNotificationPreferenceView[]>>(
    "OperatorNotificationPreferences"
  );
  return response.data;
}

export async function updateOperatorNotificationPreferences(preferences: NotificationPreferenceItemInput[]) {
  const response = await request.put<Result<boolean>>("OperatorNotificationPreferences", { preferences });
  return response.data;
}
